import {
  useLayoutEffect, useRef, useState, type JSX,
} from 'react';
import { Box } from '@mui/material';
import type { IHoverState } from '@/types/PatientDashboard/PatientTimeline.types';

interface IProps {
  hover: IHoverState;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export default function FloatingTooltip({
  hover,
  onMouseEnter,
  onMouseLeave,
}: IProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number }>();

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const viewportPadding = 8;
    const gap = 12;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = hover.right + gap;
    if (left + rect.width > viewportWidth - viewportPadding) {
      const leftPlacement = hover.left - gap - rect.width;
      left = leftPlacement >= viewportPadding
        ? leftPlacement
        : Math.max(viewportPadding, viewportWidth - rect.width - viewportPadding);
    }

    const anchorMid = (hover.top + hover.bottom) / 2;
    const top = Math.max(
      viewportPadding,
      Math.min(anchorMid - rect.height / 2, viewportHeight - rect.height - viewportPadding),
    );

    setPosition({ left, top });
  }, [hover]);

  return (
    <Box
      ref={ref}
      position="fixed"
      zIndex={1300}
      left={position?.left ?? hover.right}
      top={position?.top ?? hover.top}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        visibility: position ? 'visible' : 'hidden',
        pointerEvents: hover.isInteractive ? 'auto' : 'none',
      }}
    >
      {hover.node}
    </Box>
  );
}
